import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { FiDownload, FiShare } from 'react-icons/fi';
import { toast } from '../Toast';
import {
  CARD_FORMATS,
  canShareImages,
  downloadWorkoutImage,
  shareWorkoutImage,
  workoutCardUrl,
} from './shareWorkoutImage';
import './ShareCardModal.css';

/**
 * A workout's share card in both formats, side by side, each with Download
 * (and Share on phones and tablets). Opened by every share button: the public
 * share page, your own workout history, and a coach's view of a client's.
 *
 *   <ShareCardModal
 *     workout={w}               // needs w.token; null closes it
 *     clientName="Maria"        // a coach viewing a client's workout
 *     onClose={() => ...}
 *   />
 */
function CardPreview({ workout, format }) {
  const spec = CARD_FORMATS[format];
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(null); // 'download' | 'share'
  const src = `${workoutCardUrl(workout, format)}${attempt ? `&r=${attempt}` : ''}`;

  useEffect(() => setStatus('loading'), [src]);

  const run = (kind) => async () => {
    if (busy) return;
    setBusy(kind);
    try {
      if (kind === 'share') await shareWorkoutImage(workout, format);
      else await downloadWorkoutImage(workout, format);
    } catch (err) {
      console.error(`share card ${kind} failed`, err);
      toast.error(kind === 'share' ? 'Could not share the card. Try again.' : 'Could not download the card. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`sharecard sharecard-${format}`}>
      <div className="sharecard-frame" style={{ aspectRatio: spec.ratio }}>
        <img
          src={src}
          alt={`${workout.name || 'Workout'} share card, ${spec.label.toLowerCase()}`}
          onLoad={() => setStatus('ready')}
          onError={() => setStatus('error')}
          style={{ opacity: status === 'ready' ? 1 : 0 }}
        />
        {status !== 'ready' && (
          <div className="sharecard-state">
            {status === 'loading' ? (
              <span className="sharecard-spinner" aria-label="Making the card" />
            ) : (
              <>
                <span>Couldn’t load the card.</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAttempt((n) => n + 1)}>
                  Try again
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="sharecard-meta">
        <span className="sharecard-label">{spec.label}</span>
        <span className="sharecard-size">{spec.size}</span>
      </div>
      <div className="sharecard-actions">
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={run('download')}
          disabled={status !== 'ready' || !!busy}
        >
          <FiDownload /> {busy === 'download' ? 'Downloading…' : 'Download'}
        </button>
        {canShareImages() && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={run('share')}
            disabled={status !== 'ready' || !!busy}
          >
            <FiShare /> Share
          </button>
        )}
      </div>
    </div>
  );
}

export default function ShareCardModal({ workout, clientName, onClose }) {
  const open = !!(workout && workout.token);
  return (
    <Modal
      title={workout?.name ? `Share card · ${workout.name}` : 'Share card'}
      visible={open}
      onCancel={onClose}
      footer={null}
      width={720}
      centered
      destroyOnClose
    >
      {open && (
        <div className="sharecard-modal">
          <div className="sharecard-row">
            <CardPreview workout={workout} format="story" />
            <CardPreview workout={workout} format="post" />
          </div>
          {clientName ? (
            <p className="sharecard-note">
              This card shows {clientName}’s name and numbers. Check with them before you post it.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
